import { useMemo } from 'react'

import { matchFilterNumber, matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { AgencyTagModel, AgencyTagRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['agencyTags']

type AgencyTagFilters = {
  agency_id: number,
  string?: string,
}

type UseAgencyTagsOptions = {
  filters?: AgencyTagFilters,
  performHttpRequests?: boolean,
  requestOptions?: AgencyTagRequestOptions,
}

function useAgencyTags(options: UseAgencyTagsOptions) {
  const { filters = {}, requestOptions } = options
  const {
    // @ts-expect-error TS2339
    agency_id: filterAgencyId,
    // @ts-expect-error TS2339
    string: filterString,
  } = filters

  const {
    updatedEntities: { agencyTags: agencyTagsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { agencyTags } = useSelector(reduxState => reduxState.entities)

  const filteredAgencyTags = useMemo(() => {
    const filtered = Object.values(agencyTags).filter((agencyTag: AgencyTagModel) => {
      const { agency_id, id, title } = agencyTag

      const matchId = id.toString() === filterString
      const matchTitle = matchFilterString(title, filterString)
      const matchString = matchId || matchTitle
      const matchAgencyId = matchFilterNumber(agency_id, filterAgencyId)

      return matchAgencyId && matchString
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [agencyTagsUpdatedAt, JSON.stringify(filters)])

  const filteredAgencyTagsCount = filteredAgencyTags.length
  const hasFilteredAgencyTags = !!filteredAgencyTagsCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredAgencyTagsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingAgencyTags } = useReduxAction(
    'agencyTags',
    'loadAgencyTags',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredAgencyTags,
    filteredAgencyTagsCount,
    hasFilteredAgencyTags,
    loading: loadingAgencyTags,
  }
}

export default useAgencyTags
